"use client"

import * as React from "react"
// shadcn/ui sidebar provider primitives — used by the AppShell layout to
// wrap the AppSidebar + TopBar + main-content slot. The presentational
// sub-components (Sidebar, SidebarContent, etc.) live in
// `sub-components/AppSidebar.tsx`; `SidebarTrigger` lives in
// `sub-components/TopBar.tsx`.
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar"

import { useAppShell } from "./hooks/use-app-shell"
import { AppSidebar } from "./sub-components/AppSidebar"
import { TopBar } from "./sub-components/TopBar"
import { AdCardSidebar } from "@/components/ads/AdSlot"
// V128-AI-HUB-ASSISTANT-GLOBAL: global Asistente Hub chat button — appears
// on EVERY /app/* + /seller/* (and authed /market/* via MarketShell) route.
// Founder directive: "1 chat en cada área, en cualquier idioma, enseñando
// como a un niño". The wrapper reads `usePathname()` → injects the matching
// area's pedagogical context into the AI request as `pageContext.section`.
import { AIAssistantHub } from "@/components/ai/ai-assistant-hub"

// ---------------------------------------------------------------------------
// AppShell — the main shell that wraps authenticated pages
// ---------------------------------------------------------------------------

export function AppShell({ children }: { children: React.ReactNode }) {
  // Encapsulates the AppShell component's state + effects + handlers
  // (session-expired listener + site-maintenance redirect). The view
  // consumes no return value — both effects are side-effect only.
  // §6 Anti-Invención: cero cambios de lógica vs the original inline
  // implementation in app-shell.tsx (RT-1).
  useAppShell()

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <TopBar />
        {/* BUG-FE-027 fix (Sprint 93 QA): use div[role=main] instead of <main> to
            avoid duplicate main elements. SidebarInset (shadcn/ui sidebar.tsx)
            already renders a <main> wrapper — having a second <main> inside it
            violates WCAG 2.4.1 (one main per page). The skip-link target is
            the id="main-content", not the tag name. role="main" preserves the
            semantic for screen readers that recognize ARIA roles. */}
        <div id="main-content" role="main" className="flex-1 overflow-auto p-4 md:p-6">
          {children}
          {/* AdSense — Card 300x250 en el footer del dashboard (tier free+basic) */}
          <AdCardSidebar />
        </div>
        {/* V128-AI-HUB-ASSISTANT-GLOBAL: mounted AFTER <AdCardSidebar/> so it
            renders last in DOM order (on top). position: fixed bottom-right
            via the widget itself. Excluded on fullscreen conference/interview
            routes (own in-call chat) — see ai-assistant-hub.tsx. */}
        <AIAssistantHub />
      </SidebarInset>
    </SidebarProvider>
  )
}
